/**
 * GoHighLevel Form URL Parameter Tracking
 * 
 * Captures Google Ads URL parameters (gclid, fbclid, ValueTrack params) from the URL,
 * stores them in a cookie, and appends them to GoHighLevel form iframe URLs.
 * 
 * Based on WordPress implementation for tracking ad performance.
 */

(function() {
  'use strict';

  /** Params to exclude from storage - product/consult context and form-specific fields must not be overwritten by ad cookies */
  const EXCLUDE_FROM_STORAGE = ['notes', 'services_interested_in', 'product_id', 'variant_id'];

  /**
   * Get query parameters from the current URL (excluding form-specific params like notes)
   * @returns {Object} Object containing URL parameters for ad tracking
   */
  function getAllQueryParams() {
    const urlParams = new URLSearchParams(window.location.search);
    const params = {};
    for (const [key, value] of urlParams.entries()) {
      if (!EXCLUDE_FROM_STORAGE.includes(key)) {
        params[key] = value;
      }
    }
    return params;
  }

  /**
   * Set a cookie with the given name, value, and expiration
   * @param {string} name - Cookie name
   * @param {string} value - Cookie value
   * @param {number} days - Number of days until expiration
   */
  function setCookie(name, value, days) {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; expires=${expires.toUTCString()}; SameSite=Lax`;
  }

  /**
   * Get a cookie value by name
   * @param {string} name - Cookie name
   * @returns {string|null} Cookie value or null if not found
   */
  function getCookie(name) {
    const nameEQ = name + '=';
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i];
      while (cookie.charAt(0) === ' ') {
        cookie = cookie.substring(1, cookie.length);
      }
      if (cookie.indexOf(nameEQ) === 0) {
        return decodeURIComponent(cookie.substring(nameEQ.length, cookie.length));
      }
    }
    return null;
  }

  /**
   * Append stored parameters to GoHighLevel form iframes
   */
  function appendParamsToIframes() {
    const cookieValue = getCookie('ad_params');
    if (!cookieValue) {
      return;
    }

    try {
      const storedParams = JSON.parse(cookieValue);
      
      // Find all GHL form iframes
      const ghlIframes = document.querySelectorAll('iframe[src*="app.folsomwrapdetail.com/widget"]');
      
      ghlIframes.forEach(iframe => {
        try {
          const url = new URL(iframe.src);
          
          // Append all stored params to the iframe URL (never overwrite notes - CPC form data)
          Object.entries(storedParams).forEach(([key, value]) => {
            if (!EXCLUDE_FROM_STORAGE.includes(key) && !url.searchParams.has(key)) {
              url.searchParams.set(key, value);
            }
          });
          
          // Update iframe src
          iframe.src = url.toString();
        } catch (e) {
          console.warn('Error updating iframe URL:', e);
        }
      });
    } catch (e) {
      console.warn('Error parsing stored params:', e);
    }
  }

  /**
   * Append stored parameters to navigation links
   * @param {string} selector - CSS selector for links to modify
   */
  function appendParamsToLinks(selector) {
    const cookieValue = getCookie('ad_params');
    if (!cookieValue) {
      return;
    }

    try {
      const storedParams = JSON.parse(cookieValue);
      const links = document.querySelectorAll(selector);
      
      links.forEach(link => {
        try {
          const url = new URL(link.href, window.location.origin);
          Object.entries(storedParams).forEach(([key, value]) => {
            if (!EXCLUDE_FROM_STORAGE.includes(key)) {
              url.searchParams.set(key, value);
            }
          });
          link.href = url.toString();
        } catch (e) {
          console.warn('Error updating link URL:', e);
        }
      });
    } catch (e) {
      console.warn('Error parsing stored params for links:', e);
    }
  }

  /**
   * Intercept clicks on navigation links to preserve parameters
   */
  function interceptLinkClicks() {
    const cookieValue = getCookie('ad_params');
    if (!cookieValue) {
      return;
    }

    try {
      const storedParams = JSON.parse(cookieValue);
      
      document.addEventListener('click', function(e) {
        const link = e.target.closest('a');
        if (!link) {
          return;
        }
        
        const href = link.getAttribute('href');
        if (!href) {
          return;
        }
        
        // Check if link is to one of our target pages
        const targetPaths = ['/get-quote', '/schedule-consultation', '/book-our-services', '/book-appointment', '/contact', '/pages/consultation', '/pages/quote', '/pages/contact'];
        const isTargetLink = targetPaths.some(path => href.includes(path));
        
        if (isTargetLink && !link.hasAttribute('data-ghl-params-applied')) {
          e.preventDefault();
          
          try {
            // Build URL with params (preserve notes - CPC form data on link)
            const url = new URL(link.href, window.location.origin);
            Object.entries(storedParams).forEach(([key, value]) => {
              if (!EXCLUDE_FROM_STORAGE.includes(key)) {
                url.searchParams.set(key, value);
              }
            });
            
            // Mark link as processed and navigate
            link.setAttribute('data-ghl-params-applied', 'true');
            window.location.href = url.toString();
          } catch (e) {
            // If URL parsing fails, just follow the original link
            window.location.href = link.href;
          }
        }
      });
    } catch (e) {
      console.warn('Error setting up link interception:', e);
    }
  }

  /**
   * Initialize the tracking
   */
  function init() {
    // Capture params from URL and store in cookie
    const queryParams = getAllQueryParams();
    if (Object.keys(queryParams).length > 0) {
      setCookie('ad_params', JSON.stringify(queryParams), 90);
    }

    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        appendParamsToIframes();
        appendParamsToLinks('a[href*="/get-quote"], a[href*="/schedule-consultation"], a[href*="/book-our-services"], a[href*="/book-appointment"], a[href*="/contact"]');
        interceptLinkClicks();
      });
    } else {
      // DOM is already ready
      appendParamsToIframes();
      appendParamsToLinks('a[href*="/get-quote"], a[href*="/schedule-consultation"], a[href*="/book-our-services"], a[href*="/book-appointment"], a[href*="/contact"]');
      interceptLinkClicks();
    }

    // Also handle dynamically loaded iframes (e.g., from form_embed.js)
    // Use MutationObserver to watch for new iframes
    const observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.addedNodes.length) {
          appendParamsToIframes();
        }
      });
    });

    // Start observing
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // Initialize when script loads
  init();
})();
